import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { doc, setDoc } from "firebase/firestore";
import { Editor } from "react-draft-wysiwyg";

import React, { useEffect, useState } from "react";
import { db } from "../../../services/firebaseService/connection";
import htmlToDraft from "html-to-draftjs";
import {
  getWebsiteConfig,
  uploadImage,
} from "../../../services/firebaseService/endPoints/admin/website";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { Radio } from "antd";
import { ChantButton } from "../../../CommonComponents";
import ImageUploadButton from "./ImageUploaderButton";
import { getPrimaryColor } from "../../../utils/colors";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import AdminContainer from "../admin-container/AdminContainer";

const WebsiteMembership = () => {
  const [membershipSummary, setMembershipSummary] = useState(
    EditorState.createEmpty()
  );
  const [registrationOption, setRegistrationOption] = useState("");
  const [imageFile, setImageFile] = useState("");

  const [isSaving, setIsSaving] = useState(false);

  const adminData = useAdminDataStore(selectCurrentGroupData);

  const fontSize = undefined;

  const options = [
    {
      option: "No Registration",
      value: "no-registration",
      texts: [
        <p key="no-registration-text">
          Summary above will simply serve as a ‘welcome’. No form fields or
          ‘Join’ button. You will be required to manually pre-register person
          for Chant.
        </p>,
      ],
    },
    {
      option: "Name + Email fields",
      value: "name-email",
      texts: [
        <p key="name-email-text">
          Registration is free. Visitor enters name/email and clicks ‘Join’.
          Automatically registers new member for Chant app.
        </p>,
      ],
    },
    {
      option: "Full Registration Form",
      value: "full-registration",
      texts: [
        <p key="full-registration-text">
          Offer a full member registration page with benefits, membership
          packages, t-shirt size. Collect dues and donations. Automatically
          registers new member for Chant app.
        </p>,
        <p key="full-registration-text-2">
          Complete the form under{" "}
          <a href="/admin/members/packages/setup">
            Website / Member Registration
          </a>
          . Requires ‘Stripe’ account if collecting dues or donations.
        </p>,
      ],
    },
  ];

  const handleRadioChange = (e) => {
    setRegistrationOption(e.target.value);
  };

  const saveToFirebase = () => {
    const save = async () => {
      try {
        setIsSaving(true);

        const storageDirectory = `${adminData.groupName}/website/appScreenshots`;

        let uploadedImage = "";
        if (imageFile && typeof imageFile === "object") {
          uploadedImage = await uploadImage(
            imageFile,
            `${storageDirectory}/${adminData.id}-app-screenshot.png`
          ).then(({ downloadUrl }) => downloadUrl);
        } else if (typeof imageFile === "string" && imageFile.length > 0) {
          uploadedImage = imageFile;
        }

        const about = {
          membershipSummary: draftToHtml(
            convertToRaw(membershipSummary.getCurrentContent())
          ),
          registrationOption,
        };

        const identityFields = {
          groupName: adminData.groupName || "",
          clubName: adminData.clubName || "",
          leagueName: adminData.leagueName || "",
          country: adminData.country || "",
          ...(adminData.clubId ? { clubId: adminData.clubId } : {}),
          ...(adminData.leagueId ? { leagueId: adminData.leagueId } : {}),
        };
        let docRef = doc(db, "website", adminData.id);
        setDoc(
          docRef,
          { about, appScreenshot: uploadedImage, ...identityFields },
          { merge: true }
        ).then(() => {
          fetchData();
          postUpdateCDNUtil(adminData.id);
          setIsSaving(false);
        });
      } catch (error) {
        setIsSaving(false);
      }
    };
    save();
  };

  const getState = (websiteConfig) => {
    if (websiteConfig && websiteConfig.about) {
      let membershipSummary = EditorState.createEmpty();

      if (websiteConfig.about.membershipSummary) {
        let membershipSummaryDraft = htmlToDraft(
          websiteConfig.about.membershipSummary
        );
        if (membershipSummaryDraft) {
          let contentState = ContentState.createFromBlockArray(
            membershipSummaryDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          membershipSummary = editorState;
        }
      }

      return {
        membershipSummary,
        // membershipSummaryImage: websiteConfig.about.membershipSummaryImage,
        registrationOption: websiteConfig.about.registrationOption,
      };
    }
    return {
      membershipSummary: EditorState.createEmpty(),
    };
  };

  const fetchData = () => {
    getWebsiteConfig(adminData.id).then((data) => {
      const refactoredData = getState(data);
      setImageFile(data?.appScreenshot);
      setMembershipSummary(refactoredData?.membershipSummary);
      setRegistrationOption(refactoredData?.registrationOption);
    });
  };

  useEffect(() => {
    if (adminData?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminData.id]);

  let primaryColor = "";
  if (adminData.configuration && adminData.configuration.primaryColor) {
    primaryColor = getPrimaryColor(adminData.configuration.primaryColor);
  }

  return (
    <AdminContainer>
      <div className="chant-form-body">
        <div style={{ marginBottom: "16px" }}>
          <p style={{ marginBottom: 0 }}>Summary</p>
          <p style={{ fontSize: 12, marginBottom: "4px" }}>(1000 max characters)</p>
          <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
            <Editor
              editorState={membershipSummary}
              onEditorStateChange={(state) => setMembershipSummary(state)}
              toolbar={{
                options: ["inline", "list", "link", "fontSize"],
                inline: {
                  inDropdown: false,
                  options: ["bold", "italic", "underline"],
                },
                fontSize: {
                  icon: fontSize,
                  options: [
                    8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72,
                    96,
                  ],
                  className: undefined,
                  component: undefined,
                  dropdownClassName: undefined,
                },
                list: {
                  inDropdown: false,
                  options: ["unordered", "ordered"],
                },
                link: {
                  inDropdown: false,
                  defaultTargetOption: "_self",
                  options: ["link", "unlink"],
                  linkCallback: undefined,
                },
              }}
              stripPastedStyles={true}
              tabIndex="2"
            />
          </div>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <p style={{ marginBottom: "8px" }}>Registration Form</p>
          <Radio.Group
            onChange={handleRadioChange}
            value={registrationOption}
          >
            {options.map(({ option, value, texts }) => (
              <Radio value={value} key={value}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div className="col-md-4">{option}</div>
                  <div className="col-m4-8 ml-4">{texts}</div>
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <p style={{ marginBottom: "8px" }}>App Screenshot</p>
          <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
            <ImageUploadButton
              text="Upload"
              images={imageFile ? [imageFile] : []}
              tooltip="If no image uploaded, default image will appear. Image dimensions are 800x450 (16:9 ratio)"
              onSave={(images) => setImageFile(images[0])}
            />
            <p style={{ fontSize: "14px", color: "#6d6d6d" }}>
              Populate the app then take a screenshot to appear in the
              Membership section. Consider a matchday or when you have
              populated other content in the home feed.
            </p>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center mt-2">
        <div className="p-2">
          <ChantButton
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              width: "200px",
            }}
            loading={isSaving}
            onClick={saveToFirebase}
          >
            Save
          </ChantButton>
        </div>
      </div>
    </AdminContainer>
  );
};

export default WebsiteMembership;
